import Phaser from 'phaser';
import './style.css';
import { createGameConfig } from '@core/config/gameConfig';
import { computeDesignSize } from '@core/config/constants';
import { PLAYER_PROFILE_REGISTRY_KEY, AUDIO_REGISTRY_KEY } from '@core/scenes/BaseScene';
import { PlayerProfile } from '@core/state/PlayerProfile';
import { SaveService } from '@core/services/SaveService';
import { AudioService } from '@core/services/AudioService';
import { registerMinigames } from '@modules/index';

const GAME_PARENT_ID = 'game-root';

/** Milisegundos de espera antes de reajustar el tamaño tras un resize (evita exceso de recalculos). */
const RESIZE_DEBOUNCE_MS = 150;

/**
 * Mantiene el lienzo del juego ajustado al viewport en cualquier orientacion o tamaño,
 * recalculando el espacio de diseño cuando la ventana cambia o el dispositivo rota.
 */
function keepCanvasResponsive(game: Phaser.Game): void {
  let timer: number | undefined;

  const applySize = (): void => {
    const size = computeDesignSize(window.innerWidth, window.innerHeight);
    game.scale.setGameSize(size.width, size.height);
  };

  const scheduleResize = (): void => {
    window.clearTimeout(timer);
    timer = window.setTimeout(applySize, RESIZE_DEBOUNCE_MS);
  };

  window.addEventListener('resize', scheduleResize);
  window.addEventListener('orientationchange', scheduleResize);
}

/**
 * Registra el service worker (solo en produccion) para que la app sea instalable
 * como PWA y funcione sin conexion luego de la primera carga.
 */
function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) {
    return;
  }
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('sw.js').catch(() => undefined);
  });
}

/** Arranque de la aplicacion: registra minijuegos, crea el juego e inyecta el perfil. */
function bootstrap(): void {
  registerMinigames();
  registerServiceWorker();

  const game = new Phaser.Game(createGameConfig(GAME_PARENT_ID));
  keepCanvasResponsive(game);

  const playerProfile = new PlayerProfile(new SaveService());
  game.registry.set(PLAYER_PROFILE_REGISTRY_KEY, playerProfile);

  const audioService = new AudioService();
  audioService.attachSound(game.sound);
  game.registry.set(AUDIO_REGISTRY_KEY, audioService);

  // Los navegadores bloquean el audio hasta que el usuario interactua: habilitamos el
  // audio en el primer toque/click (ahi arranca la musica de la escena actual).
  const unlockAudioOnce = (): void => {
    audioService.unlock();
    window.removeEventListener('pointerdown', unlockAudioOnce);
  };
  window.addEventListener('pointerdown', unlockAudioOnce);

  // Solo en desarrollo: exponer el juego para depurar desde la consola del navegador
  // (por ejemplo, game.scene.start('AdventureScene')). No se incluye en produccion.
  if (import.meta.env.DEV) {
    (window as unknown as { game?: Phaser.Game }).game = game;
  }
}

bootstrap();
